import { Link, useNavigate } from 'react-router-dom';
import { FiEye, FiCalendar, FiTag, FiEdit2, FiTrash2, FiClock } from 'react-icons/fi';
import storageService from '../services/storageService';
import { SyncStatus } from '../services/syncService';

const PostCard = ({ post, onDelete }) => {
  const navigate = useNavigate();
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Determinar si el post está en revisión
  const isPending = post.syncStatus === SyncStatus.PENDING;
  const canEdit = !isPending; // No se puede editar si está pendiente

  const handleEdit = () => {
    if (!canEdit) {
      alert('⏳ No puedes editar un post que está en revisión.\n\nEspera a que sea aprobado o rechazado.');
      return;
    }
    navigate(`/posts/editar/${post.slug}`);
  };

  const handleDelete = async () => {
    if (isPending) {
      if (!confirm('⚠️ Este post está en revisión.\n¿Estás seguro de que quieres eliminarlo?\n\nSi lo eliminas, deberás enviarlo nuevamente.')) return;
    } else {
      if (!confirm('¿Eliminar este post local?')) return;
    }

    try {
      const localId = parseInt(post.slug.replace('local_', ''));
      await storageService.posts.eliminar(localId);
      if (onDelete) onDelete(localId);
      alert('✅ Post eliminado');
    } catch (error) {
      console.error('Error eliminando post:', error);
      alert('Error al eliminar el post');
    }
  };

  // Función para obtener el badge de estado
  const getStatusBadge = () => {
    if (!post.esLocal) return null;
    
    switch (post.syncStatus) {
      case SyncStatus.PENDING:
        return (
          <span className="inline-flex items-center gap-1 text-xs px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full font-medium">
            <FiClock className="w-3 h-3" />
            Pendiente de Aprobación
          </span>
        );
      case SyncStatus.LOCAL:
        return (
          <span className="text-xs px-3 py-1 bg-purple-100 text-purple-700 rounded-full font-medium">
            💾 Borrador Local
          </span>
        );
      default:
        return (
          <span className="text-xs px-3 py-1 bg-purple-100 text-purple-700 rounded-full font-medium">
            💾 Local
          </span>
        );
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden">
      {post.imagen && (
        <div className="h-48 overflow-hidden">
          <img
            src={post.imagen}
            alt={post.titulo}
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
          />
        </div>
      )}
      
      <div className="p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2 hover:text-indigo-600">
          <Link to={`/estudios/${post.slug}`}>{post.titulo}</Link>
        </h3>
        
        <p className="text-gray-600 text-sm mb-4 line-clamp-3">
          {post.descripcion}
        </p>

        {/* Categorías */}
        {post.categorias && post.categorias.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {post.categorias.slice(0, 3).map((categoria) => (
              <span
                key={categoria.id}
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                style={{
                  backgroundColor: `${categoria.color}20`,
                  color: categoria.color
                }}
              >
                <FiTag className="mr-1" size={12} />
                {categoria.nombre}
              </span>
            ))}
            {post.categorias.length > 3 && (
              <span className="text-xs text-gray-500">
                +{post.categorias.length - 3} más
              </span>
            )}
          </div>
        )}

        {/* Metadata */}
        <div className="flex items-center justify-between text-sm text-gray-500 border-t pt-4">
          <div className="flex items-center space-x-4">
            <span className="flex items-center">
              <FiCalendar className="mr-1" size={14} />
              {formatDate(post.fechaPublicacion || post.createdAt)}
            </span>
            {!post.esLocal && (
              <span className="flex items-center">
                <FiEye className="mr-1" size={14} />
                {post.visualizaciones || 0} vistas
              </span>
            )}
            {getStatusBadge()}
          </div>
          
          <div className="flex items-center gap-2">
            {post.esLocal ? (
              <>
                <button
                  onClick={handleEdit}
                  disabled={!canEdit}
                  className={`p-1 rounded transition-colors ${
                    canEdit 
                      ? 'text-blue-600 hover:text-blue-800 hover:bg-blue-50' 
                      : 'text-gray-400 cursor-not-allowed opacity-50'
                  }`}
                  title={canEdit ? "Editar" : "No se puede editar (en revisión)"}
                >
                  <FiEdit2 size={16} />
                </button>
                <button
                  onClick={handleDelete}
                  className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50"
                  title="Eliminar"
                >
                  <FiTrash2 size={16} />
                </button>
              </>
            ) : (
              <Link
                to={`/estudios/${post.slug}`}
                className="text-indigo-600 hover:text-indigo-800 font-medium"
              >
                Leer más →
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostCard;










